import { createElement, lazy } from "react";
import { Route, Routes } from "react-router-dom";

/** Today's pages, for the switchboard's `legacy` side (and /legacy/<path> after a release). */
export const LegacyContactsPage = lazy(() => import("../../pages/ContactsPage").then((m) => ({ default: m.ContactsPage })));
export const LegacyCreateHouseholdPage = lazy(() => import("../../pages/CreateHouseholdPage").then((m) => ({ default: m.CreateHouseholdPage })));
export const LegacyEditHouseholdPage = lazy(() => import("../../pages/UpdateHouseholdInfoPage"));
export const LegacyNotFoundPage = lazy(() => import("../../pages/404/404").then((m) => ({ default: m.Error404Page })));
export const LegacyTeamPage = lazy(() => import("../../pages/TeamPage").then((m) => ({ default: m.TeamPage })));

/** Today's saved-routes page (the list, with its side panel). The switchboard's `legacy` side of /saved-routes. */
export const LegacySavedRoutesPage = lazy(() => import("../../pages/SavedRoutesPage").then((m) => ({ default: m.SavedRoutesPage })));

/**
 * Today's route page inside today's saved-routes layout (its side panel, which draws its page through an Outlet): the switchboard's
 * `legacy` side of /saved-routes/:id. The nested routes use the parent's params, so today's page still reads `:id`.
 */
export const LegacySavedRoutePage = lazy(() => Promise.all([import("../../components/SavedRoutesPage/SavedRoutesLayout"), import("../../pages/RoutePage")])
  .then(([layout, page]) => ({
    default: () => createElement(Routes, null, createElement(Route, { element: createElement(layout.SavedRoutesLayout) }, createElement(Route, { index: true, element: createElement(page.RoutePage) }))),
  })));

/** Today's Map behind today's guard (`read_map`), exactly as /map was wired before the switchboard. The `legacy` side of /map. */
export const LegacyMapPage = lazy(() => Promise.all([import("../../hocs/permissionRoute"), import("../../pages/HomePage/HomePage")])
  .then(([guard, page]) => ({
    default: () => createElement(guard.PermissionRoute, { permission: "read_map", children: createElement(page.HomePage) }),
  })));

/**
 * Today's Reports page behind both of today's guards, exactly as /duchot was wired in App before the switchboard: not for a
 * volunteer-only account (`AccountRoute`) and only with `read_duchot` (`PermissionRoute`, the menu entry's own permission).
 * The `legacy` side of /duchot.
 */
export const LegacyReportsPage = lazy(() => Promise.all([import("../../hocs/accountRoute"), import("../../hocs/permissionRoute"), import("../../hocs/guardedPaths"), import("../../pages/VisitPage")])
  .then(([account, guard, paths, page]) => ({
    default: () => createElement(account.AccountRoute, { children: createElement(guard.PermissionRoute, { ...paths.guardFor("/duchot"), children: createElement(page.VisitPage) }) }),
  })));

/** Today's Billing page behind today's guard (`read_billing`), exactly as /billing was wired in App before the switchboard. The `legacy` side of /billing. */
export const LegacyBillingPage = lazy(() => Promise.all([import("../../hocs/permissionRoute"), import("../../pages/BillingPage")])
  .then(([guard, page]) => ({
    default: () => createElement(guard.PermissionRoute, { permission: "read_billing", children: createElement(page.BillingPage) }),
  })));

/** Today's pricing page (open to every signed-in person). The `legacy` side of /pricing. */
export const LegacyPricingPage = lazy(() => import("../../pages/PricingPage"));

/**
 * Today's Dashboard behind today's guard (`RoleRoute` with the dashboard roles), exactly as /dashboard was wired in App before the
 * switchboard. The `legacy` side of /dashboard.
 */
export const LegacyDashboardPage = lazy(() => Promise.all([import("../../hocs/roleRoute"), import("../../utils/permissions"), import("../../pages/DashboardPage")])
  .then(([guard, perms, page]) => ({
    default: () => createElement(guard.RoleRoute, { roles: perms.DASHBOARD_ROLES, children: createElement(page.DashboardPage) }),
  })));

/** Today's Settings page behind today's guard (`AccountRoute`), as /settings was wired in App before the switchboard. The `legacy` side of /settings. */
export const LegacySettingsPage = lazy(() => Promise.all([import("../../hocs/accountRoute"), import("../../pages/SettingsPage")])
  .then(([guard, page]) => ({
    default: () => createElement(guard.AccountRoute, { children: createElement(page.SettingsPage) }),
  })));

/** Today's Volunteering page behind `manage_volunteering`. The `legacy` side of /volunteering. */
export const LegacyVolunteeringPage = lazy(() => Promise.all([import("../../hocs/permissionRoute"), import("../../pages/VolunteeringPage")])
  .then(([guard, page]) => ({
    default: () => createElement(guard.PermissionRoute, { permission: "manage_volunteering", children: createElement(page.VolunteeringPage) }),
  })));

/** Today's Projects page behind `manage_projects`. The `legacy` side of /projects. */
export const LegacyProjectsPage = lazy(() => Promise.all([import("../../hocs/permissionRoute"), import("../../pages/ProjectsPage")])
  .then(([guard, page]) => ({
    default: () => createElement(guard.PermissionRoute, { permission: "manage_projects", children: createElement(page.default) }),
  })));
